import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import { useSelector, useDispatch } from "react-redux";
import {
  setRangeSlide,
  setCurrentSlide,
  setMinRangeSlide,
  setCurrentMinSlide,
} from "../../Redux/filterSlice";
import { FormatCurrency, FormatPoints } from "../../lib";
import { trackClick } from "../../utils/gtm";
import { tagIdFromLegacySuffix } from "../../utils/marketplaceTagIds";
import { useScreenTracking } from "../../hooks/useScreenTracking";

const HorizontalStack = ({ children, ...props }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={0}
    {...props}
  >
    {children}
  </Stack>
);

const VerticalStack = ({ children, ...props }) => (
  <Stack
    direction="column"
    justifyContent="center"
    alignItems="center"
    spacing={0}
    className="font-wl-400 text-sm"
    {...props}
  >
    {children}
  </Stack>
);
export default function SliderRangeFilter({ mode = "both", brands = [] }) {
  const {
    greaterValue,
    lowerValue,
    rangeSlide,
    currentSlide,
    greaterValuePoints,
    lowerValuePoints,
    factor,
    initialFactor,
    initialGreaterValue,
    initialLowerValue,
    initialGreaterValuePoints,
    initialLowerValuePoints,
    minRangeSlide,
    currentMinSlide,
  } = useSelector((state) => state.filterSlice);

  const [currencyMode, setCurrencyMode] = useState(
    mode === "both" ? "money" : mode,
  );

  const getCurrentValues = () => {
    const moneyMin = Number(initialLowerValue) || Number(lowerValue) || 0;
    const moneyMax = Number(initialGreaterValue) || Number(greaterValue) || 0;
    const pointsMin =
      Number(initialLowerValuePoints) || Number(lowerValuePoints) || 0;
    const pointsMax =
      Number(initialGreaterValuePoints) || Number(greaterValuePoints) || 0;

    if (currencyMode === "points") {
      // Range slider with two values: [min selected, max selected]
      const currentMinPoints = Number(currentMinSlide) || pointsMin;
      const currentMaxPoints = Number(currentSlide) || pointsMax;

      return {
        boundMin: pointsMin,
        boundMax: pointsMax,
        currentMin: currentMinPoints,
        currentMax: currentMaxPoints,
        rangeValue: [currentMinPoints, currentMaxPoints],
      };
    }
    // Money mode
    const currentMinMoney = Number(minRangeSlide) || moneyMin;
    const currentMaxMoney = Number(rangeSlide) || moneyMax;

    return {
      boundMin: moneyMin,
      boundMax: moneyMax,
      currentMin: currentMinMoney,
      currentMax: currentMaxMoney,
      rangeValue: [currentMinMoney, currentMaxMoney],
    };
  };

  const currentValues = getCurrentValues();
  const screenTracking = useScreenTracking();

  const formatValue = (val) => {
    if (currencyMode === "points") {
      return `${FormatPoints(val)} pts`;
    }
    return FormatCurrency(val);
  };

  const disp = useDispatch();

  const [activeThumbIndex, setActiveThumbIndex] = useState(null);

  const handleChange = (event, newValue, activeThumb) => {
    trackClick({
      title: `valor-${newValue?.[0] ?? 0}`,
      contentType: brands?.[0]?.label ?? "brand",
      screenName: screenTracking,
      tagId: tagIdFromLegacySuffix("05"),
    });

    setActiveThumbIndex(activeThumb);
    const moneyMin = Number(initialLowerValue) || Number(lowerValue) || 0;
    const moneyMax = Number(initialGreaterValue) || Number(greaterValue) || 0;
    const pointsMin =
      Number(initialLowerValuePoints) || Number(lowerValuePoints) || 0;
    const pointsMax =
      Number(initialGreaterValuePoints) || Number(greaterValuePoints) || 0;
    const safeFactor = Number(initialFactor) || Number(factor) || 1;

    // newValue is now an array [minValue, maxValue]
    const [minVal, maxVal] = Array.isArray(newValue)
      ? newValue
      : [moneyMin, newValue];

    if (currencyMode === "points") {
      // Clamp in points space
      let minPoints = Math.round(Number(minVal));
      let maxPoints = Math.round(Number(maxVal));

      if (minPoints < pointsMin) minPoints = pointsMin;
      if (maxPoints > pointsMax) maxPoints = pointsMax;

      // Ensure min <= max
      if (minPoints > maxPoints) minPoints = maxPoints;

      // Convert to money for storage
      const minMoney = minPoints / safeFactor;
      const maxMoney = maxPoints / safeFactor;

      disp(setMinRangeSlide({ value: minMoney }));
      disp(setRangeSlide({ value: maxMoney }));
      disp(setCurrentMinSlide({ value: minPoints }));
      disp(setCurrentSlide({ value: maxPoints }));
    } else {
      let clampedMin = Number(minVal);
      let clampedMax = Number(maxVal);

      if (clampedMin < moneyMin) clampedMin = moneyMin;
      if (clampedMax > moneyMax) clampedMax = moneyMax;

      // Ensure min <= max
      if (clampedMin > clampedMax) clampedMin = clampedMax;

      disp(setMinRangeSlide({ value: clampedMin }));
      disp(setRangeSlide({ value: clampedMax }));
      disp(setCurrentMinSlide({ value: clampedMin }));
      disp(setCurrentSlide({ value: clampedMax }));
    }
  };

  useEffect(() => {
    const moneyMin = Number(initialLowerValue) || Number(lowerValue) || 0;
    const moneyMax = Number(initialGreaterValue) || Number(greaterValue) || 0;
    const pointsMin =
      Number(initialLowerValuePoints) || Number(lowerValuePoints) || 0;
    const pointsMax =
      Number(initialGreaterValuePoints) || Number(greaterValuePoints) || 0;

    const currentMinMoney = Number(minRangeSlide);
    const currentMaxMoney = Number(rangeSlide);
    const currentMinPoints = Number(currentMinSlide);
    const currentMaxPoints = Number(currentSlide);

    // Inicializar valores mínimos se não estiverem definidos
    if (!Number.isFinite(currentMinMoney) || currentMinMoney === 0) {
      if (Number.isFinite(moneyMin) && moneyMin > 0) {
        disp(setMinRangeSlide({ value: moneyMin }));
      }
    }

    // Inicializar valores máximos se não estiverem definidos
    if (!Number.isFinite(currentMaxMoney) || currentMaxMoney === 0) {
      if (Number.isFinite(moneyMax) && moneyMax > 0) {
        disp(setRangeSlide({ value: moneyMax }));
      }
    }

    // Inicializar valores de pontos
    if (!Number.isFinite(currentMinPoints) || currentMinPoints === 0) {
      if (Number.isFinite(pointsMin) && pointsMin > 0) {
        disp(setCurrentMinSlide({ value: pointsMin }));
      }
    }

    if (!Number.isFinite(currentMaxPoints) || currentMaxPoints === 0) {
      if (Number.isFinite(pointsMax) && pointsMax > 0) {
        disp(setCurrentSlide({ value: pointsMax }));
      }
    }

    // Validar limites
    if (currentMinMoney > 0 && currentMinMoney < moneyMin) {
      disp(setMinRangeSlide({ value: moneyMin }));
    }
    if (currentMaxMoney > 0 && currentMaxMoney > moneyMax) {
      disp(setRangeSlide({ value: moneyMax }));
    }
  }, [
    initialLowerValue,
    initialGreaterValue,
    initialLowerValuePoints,
    initialGreaterValuePoints,
    lowerValue,
    greaterValue,
    greaterValuePoints,
    lowerValuePoints,
  ]);

  return (
    <div>
      <p className="font-wl-600 text-mlg color-text-dark mb-2">
        {currencyMode === "points" ? "Valor" : "Preço"}
      </p>

      {mode === "both" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "12px",
          }}
        >
          <span>Pontos</span>
          <div
            onClick={() =>
              setCurrencyMode(currencyMode === "money" ? "points" : "money")
            }
            style={{
              width: "40px",
              height: "20px",
              borderRadius: "20px",
              backgroundColor: currencyMode === "money" ? "#24283E" : "#ccc",
              position: "relative",
              cursor: "pointer",
              transition: "background-color 0.3s",
            }}
          >
            <div
              style={{
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                backgroundColor: "#fff",
                position: "absolute",
                top: "1px",
                left: currencyMode === "money" ? "20px" : "2px",
                transition: "left 0.3s",
              }}
            />
          </div>
          <span>Reais</span>
        </div>
      )}

      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span className="font-wl-400 text-sm">
            {formatValue(currentValues.currentMin)}
          </span>
          <span className="font-wl-400 text-sm">
            {formatValue(currentValues.currentMax)}
          </span>
        </div>
        <div style={{ position: "relative", width: "100%", padding: "0 11px" }}>
          {activeThumbIndex !== null &&
            (() => {
              const value = currentValues.rangeValue[activeThumbIndex];
              const range = currentValues.boundMax - currentValues.boundMin;
              const percent =
                range > 0
                  ? ((value - currentValues.boundMin) / range) * 100
                  : 0;
              const edgeThreshold = 15;
              let translateX;
              if (percent <= edgeThreshold) {
                translateX = -(percent / edgeThreshold) * 50;
              } else if (percent >= 100 - edgeThreshold) {
                translateX =
                  -50 -
                  ((percent - (100 - edgeThreshold)) / edgeThreshold) * 50;
              } else {
                translateX = -50;
              }
              return (
                <div
                  style={{
                    position: "absolute",
                    top: "-28px",
                    left: `${percent}%`,
                    transform: `translateX(${translateX}%)`,
                    backgroundColor: "#24283E",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: "600",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    whiteSpace: "nowrap",
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                >
                  {formatValue(value)}
                </div>
              );
            })()}
          <Slider
            value={currentValues.rangeValue}
            onChange={handleChange}
            onChangeCommitted={() => setActiveThumbIndex(null)}
            min={currentValues.boundMin}
            max={currentValues.boundMax}
            valueLabelDisplay="off"
            disableSwap
            sx={{
              width: "100%",
              padding: "13px 0",
              color: `var(--primary-color)`,
              ".MuiSlider-rail": {
                color: "#E0E0E0",
                opacity: 1,
                height: "2px",
              },
              ".MuiSlider-thumb": {
                width: "22px",
                height: "22px",
                backgroundColor: "#FFFFFF",
                border: "2px solid var(--primary-color)",
                "&:hover": {
                  boxShadow: "none",
                },
                "&.Mui-active": {
                  boxShadow: "none",
                },
                "&.Mui-focusVisible": {
                  boxShadow: "none",
                },
              },
              ".MuiSlider-track": {
                color: `var(--primary-color)`,
                height: "2px",
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

HorizontalStack.propTypes = {
  children: PropTypes.node,
};

VerticalStack.propTypes = {
  children: PropTypes.node,
};

SliderRangeFilter.propTypes = {
  mode: PropTypes.oneOf(["both", "money", "points"]),
  brands: PropTypes.array,
};
